import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useVerifyEmailChange } from '@/api/hooks/useProfile';
import { useAuth } from '@/contexts/AuthContext';
import { LogoFull } from '@/components/LogoFull';
import { Button } from '@/components/ui/Button';

export function EmailChangeVerifyPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(
        token ? 'idle' : 'error'
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(
        token ? null : 'Invalid or missing verification token.'
    );
    const [newEmail, setNewEmail] = useState<string | null>(null);

    const verifyMutation = useVerifyEmailChange();
    const { logout } = useAuth();

    async function handleVerify() {
        setStatus('loading');
        verifyMutation.mutate(token, {
            onSuccess: async (data) => {
                try {
                    await logout();
                } catch {
                    // The email change already succeeded, so a failed logout must not hide that.
                }
                setNewEmail(data.newEmail ?? null);
                setStatus('success');
            },
            onError: (err) => {
                setStatus('error');
                if (err instanceof Error) {
                    if (err.message.includes('expired')) {
                        setErrorMessage('This verification link has expired. Please request a new email change.');
                    } else if (err.message.includes('invalid') || err.message.includes('not found')) {
                        setErrorMessage('This verification link is invalid. Please request a new email change.');
                    } else if (err.message.includes('no longer available')) {
                        setErrorMessage('This email address is no longer available. Please request a new email change with a different address.');
                    } else {
                        setErrorMessage(err.message || 'Failed to verify email change. Please try again.');
                    }
                } else {
                    setErrorMessage('An unexpected error occurred');
                }
            },
        });
    }

    if (status === 'idle') {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <Link to="/">
                            <LogoFull className="mx-auto h-10 w-auto" />
                        </Link>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Complete Email Change
                        </h1>

                        <p className="mb-6 text-center text-sm text-gray-600 dark:text-gray-400">
                            Click the button below to complete your email change.
                            After this, you'll need to log in again with your new email address.
                        </p>

                        <Button
                            type="button"
                            onClick={handleVerify}
                            className="w-full"
                            size="lg"
                        >
                            Complete Email Change
                        </Button>

                        <div className="mt-6 text-center">
                            <Link
                                to="/profile"
                                className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                                Cancel
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (status === 'loading') {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <Link to="/">
                            <LogoFull className="mx-auto h-10 w-auto" />
                        </Link>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Verifying new email...
                        </h1>
                        <div className="flex justify-center">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <Link to="/">
                            <LogoFull className="mx-auto h-10 w-auto" />
                        </Link>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Email Changed Successfully
                        </h1>

                        <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-4 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                            <p className="font-medium">Your email has been changed.</p>
                            {newEmail && (
                                <p className="mt-1 text-sm">
                                    Your new email is: <span className="font-medium">{newEmail}</span>
                                </p>
                            )}
                        </div>

                        <Link to="/login" className="block">
                            <Button type="button" className="w-full" size="lg">
                                Go to login
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Error state (also handles missing token)
    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <Link to="/">
                        <LogoFull className="mx-auto h-10 w-auto" />
                    </Link>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                    <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                        Verification failed
                    </h1>

                    <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                        {errorMessage}
                    </div>

                    <Link to="/profile" className="block">
                        <Button type="button" className="w-full" size="lg">
                            Back to profile
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
